import { Button, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { projectSlice } from '../../../redux/ProjectSlice';
import { RootState } from '../../../redux/Store';
import { Path } from '../../PathBuilder/pathBuilderTypes';
import checkPathName from '../../../utils/validation/checkPathName';
import { find } from 'ramda';

interface PathCreationDialogProps {
  onClose: () => void;
  open: boolean;
}

const PathCreationDialog = ({ onClose, open }: PathCreationDialogProps) => {
  const { isEditing, pathId } = useSelector((state: RootState) => state.application.openDialog.options);
  const [pathName, setPathName] = useState<string>('');
  const [path, setPath] = useState<Path>();
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { loadedProject } = useSelector((state: RootState) => state.project);

  const findPath = useCallback(
    (id: string) => {
      if (!loadedProject) return;
      const path = find((p: Path) => p.id === id, loadedProject.paths);
      return path;
    },
    [loadedProject]
  );

  useEffect(() => {
    if (open) {
      if (!pathId) return;
      const _path = findPath(pathId);
      setPath(_path);
      if (isEditing) {
        const workingName = _path?.name || '';
        setPathName(workingName);
      }
    }
  }, [open, isEditing, pathId, findPath]);

  useEffect(() => {
    if (!open) return;

    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [open]);

  const handleInput = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPathName(event.target.value);
  };

  const handleCreatePath = () => {
    const defaultWaypoints = [
      { id: crypto.randomUUID(), x: 30, y: 30, radius: 0, rotation: 0 },
      { id: crypto.randomUUID(), x: 100, y: 30, radius: 0, rotation: 30 },
    ];
    const newPath: Path = {
      name: pathName,
      id: path?.id || self.crypto.randomUUID(),
      isVisible: path?.isVisible || true,
      waypoints: path?.waypoints || defaultWaypoints,
      segmentSpeeds: path?.segmentSpeeds || [1],
    };

    const checkedName = checkPathName(pathName, newPath.id);
    if (checkedName) newPath.name = checkedName;

    if (!loadedProject) return;
    let _paths: Array<Path>;
    if (findPath(newPath.id)) {
      _paths = loadedProject.paths.map((p: Path) => (p.id === newPath.id ? newPath : p));
    } else {
      _paths = [newPath, ...loadedProject.paths];
    }

    dispatch(projectSlice.actions.onUpdatePaths(_paths));
    dispatch(projectSlice.actions.onUpdateSelectedPathId(newPath.id));
    onClose();
  };

  const handleSubmitPath = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleCreatePath();
  };

  const handleCancelPath = () => {
    onClose();
  };

  return (
    <>
      <DialogTitle textAlign="center">
        <Trans i18nKey={'pathCreationDialog.name-your-path'}>Name Your Path</Trans>
      </DialogTitle>
      <DialogContent>
        <Stack component="form" direction="column" spacing={2} marginTop={1} onSubmit={handleSubmitPath}>
          <TextField
            autoFocus
            id="path-name"
            inputRef={inputRef}
            label="Path Name"
            placeholder={t('pathCreationDialog.input.placeholder', 'Untitled Path')}
            value={pathName}
            onChange={handleInput}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Stack direction="row" display="flex" justifyContent="space-between">
            <Button type="button" onClick={handleCancelPath}>
              <Trans i18nKey="pathCreationDialog.button.cancel">Cancel</Trans>
            </Button>

            <Button type="submit">
              <Trans i18nKey="pathCreationDialog.button.create">Create</Trans>
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );
};

export default PathCreationDialog;
